import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown, Server, Database, Cloud, Shield, Award, Users, Mail, Phone, Linkedin, Brain, Network, Lock, HardDrive, RefreshCw } from 'lucide-react';
import './App.css';
import Nosotros from './pages/Nosotros';
import Soluciones from './pages/Soluciones';
import CasosDeUso from './pages/CasosDeUso';
import Contacto from './pages/Contacto';
// Import solution pages
import NubeHibrida from './pages/soluciones/NubeHibrida';
import GestionDatos from './pages/soluciones/GestionDatos';
import InteligenciaArtificial from './pages/soluciones/InteligenciaArtificial';
import Conectividad from './pages/soluciones/Conectividad';
import Nube from './pages/soluciones/Nube';
import Seguridad from './pages/soluciones/Seguridad';
import ServiciosGestionados from './pages/soluciones/ServiciosGestionados';

// Navbar Component
function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [solucionesDropdownOpen, setSolucionesDropdownOpen] = React.useState(false);
  const location = useLocation();
  const dropdownRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  const isActive = (path) => location.pathname === path;
  const isSolucionesActive = location.pathname.startsWith('/soluciones');

  // Soluciones data for mega menu
  const soluciones = [
    {
      title: 'Nube Híbrida + IA Agéntica',
      description: 'Infraestructura como servicio con optimización inteligente',
      icon: Cloud,
      path: '/soluciones/nube-hibrida',
      color: 'blue'
    },
    {
      title: 'Inteligencia Artificial',
      description: 'Soluciones de IA empresarial y Private Cloud AI',
      icon: Brain,
      path: '/soluciones/inteligencia-artificial',
      color: 'purple'
    },
    {
      title: 'Conectividad Empresarial',
      description: 'Redes inteligentes, SASE y seguridad perimetral',
      icon: Network,
      path: '/soluciones/conectividad',
      color: 'green'
    },
    {
      title: 'Nube y CloudOps',
      description: 'Gestión multi-cloud y modernización de aplicaciones',
      icon: Database,
      path: '/soluciones/nube',
      color: 'indigo'
    },
    {
      title: 'Seguridad',
      description: 'Protección integral desde el firmware hasta la nube',
      icon: Lock,
      path: '/soluciones/seguridad',
      color: 'red'
    },
    {
      title: 'Gestión de Datos',
      description: 'Almacenamiento, protección y resiliencia de datos',
      icon: HardDrive,
      path: '/soluciones/datos',
      color: 'teal'
    },
    {
      title: 'Servicios Gestionados',
      description: 'Operación y soporte 24/7 de tu infraestructura',
      icon: RefreshCw,
      path: '/soluciones/servicios-gestionados',
      color: 'orange'
    }
  ];

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setSolucionesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-white shadow-md fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/Allti - Logo Full.png" 
              alt="ALLTI" 
              className="h-8 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className={`${isActive('/') ? 'text-allti-blue border-b-2 border-allti-blue' : 'text-gray-700 hover:text-allti-blue'} pb-1 transition-colors font-medium`}>
              Inicio
            </Link>
            <Link to="/nosotros" className={`${isActive('/nosotros') ? 'text-allti-blue border-b-2 border-allti-blue' : 'text-gray-700 hover:text-allti-blue'} pb-1 transition-colors font-medium`}>
              Nosotros
            </Link>
            
            {/* Soluciones Dropdown */}
            <div className="relative">
              <button 
                ref={buttonRef}
                onClick={() => setSolucionesDropdownOpen(!solucionesDropdownOpen)}
                onMouseEnter={() => setSolucionesDropdownOpen(true)}
                className={`${isSolucionesActive ? 'text-allti-blue border-b-2 border-allti-blue' : 'text-gray-700 hover:text-allti-blue'} pb-1 transition-colors font-medium flex items-center space-x-1`}
              >
                <span>Soluciones</span>
                <ChevronDown size={16} className={`transform transition-transform ${solucionesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Mega Menu Dropdown */}
              {solucionesDropdownOpen && (
                <div 
                  ref={dropdownRef}
                  className="fixed left-0 right-0 top-20 bg-white border-t border-gray-100 shadow-2xl z-50"
                  onMouseLeave={() => setSolucionesDropdownOpen(false)}
                  onMouseEnter={() => setSolucionesDropdownOpen(true)}
                >
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-6">
                      <Link 
                        to="/soluciones"
                        onClick={() => setSolucionesDropdownOpen(false)}
                        className="inline-flex items-center text-allti-blue font-semibold hover:underline text-lg"
                      >
                        Ver todas las soluciones <ChevronRight size={20} className="ml-1" />
                      </Link>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {soluciones.map((solucion, index) => {
                        const Icon = solucion.icon;
                        const colorClasses = {
                          blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
                          purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
                          green: 'bg-green-50 text-green-600 group-hover:bg-green-100',
                          indigo: 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100',
                          red: 'bg-red-50 text-red-600 group-hover:bg-red-100',
                          teal: 'bg-teal-50 text-teal-600 group-hover:bg-teal-100',
                          orange: 'bg-orange-50 text-orange-600 group-hover:bg-orange-100'
                        };
                        
                        return (
                          <Link
                            key={index}
                            to={solucion.path}
                            onClick={() => setSolucionesDropdownOpen(false)}
                            className="group bg-white hover:bg-gray-50 p-6 rounded-xl border border-gray-200 hover:border-allti-blue transition-all hover:shadow-lg"
                          >
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-all ${colorClasses[solucion.color]}`}>
                              <Icon size={24} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-allti-blue transition-colors">
                              {solucion.title}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {solucion.description}
                            </p>
                          </Link>
                        );
                      })}
                    </div>
                    
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">¿No encuentras lo que buscas?</p>
                          <p className="text-sm font-semibold text-gray-900">Habla con nuestros expertos</p>
                        </div>
                        <Link
                          to="/contacto"
                          onClick={() => setSolucionesDropdownOpen(false)}
                          className="bg-allti-red text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          Contactar
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Link to="/casos-de-uso" className={`${isActive('/casos-de-uso') ? 'text-allti-blue border-b-2 border-allti-blue' : 'text-gray-700 hover:text-allti-blue'} pb-1 transition-colors font-medium`}>
              Casos de Uso
            </Link>
            <Link to="/contacto" className="bg-allti-red text-white px-6 py-2.5 rounded-lg hover:bg-red-800 transition-colors font-medium flex items-center">
              Contacto
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => { setIsOpen(!isOpen); setSolucionesDropdownOpen(false); }} className="md:hidden text-gray-700">
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-4 space-y-3">
            <Link to="/" onClick={() => setIsOpen(false)} className="block text-gray-700 hover:text-allti-blue font-medium">
              Inicio
            </Link>
            <Link to="/nosotros" onClick={() => setIsOpen(false)} className="block text-gray-700 hover:text-allti-blue font-medium">
              Nosotros
            </Link>
            
            {/* Mobile Soluciones Submenu */}
            <div>
              <button 
                onClick={() => setSolucionesDropdownOpen(!solucionesDropdownOpen)}
                className="w-full text-left text-gray-700 hover:text-allti-blue font-medium flex items-center justify-between"
              >
                <span>Soluciones</span>
                <ChevronDown size={16} className={`transform transition-transform ${solucionesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {solucionesDropdownOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  <Link to="/soluciones" onClick={() => { setSolucionesDropdownOpen(false); setIsOpen(false); }} className="block text-sm text-gray-600 hover:text-allti-blue py-1 font-medium">
                    Ver todas
                  </Link>
                  {soluciones.map((solucion, index) => (
                    <Link 
                      key={index}
                      to={solucion.path} 
                      onClick={() => { setSolucionesDropdownOpen(false); setIsOpen(false); }} 
                      className="block text-sm text-gray-600 hover:text-allti-blue py-1"
                    >
                      {solucion.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link to="/casos-de-uso" onClick={() => setIsOpen(false)} className="block text-gray-700 hover:text-allti-blue font-medium">
              Casos de Uso
            </Link>
            <Link to="/contacto" onClick={() => setIsOpen(false)} className="block bg-allti-red text-white px-6 py-2 rounded-lg text-center font-medium">
              Contacto
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// Footer Component
function Footer() {
  return (
    <footer className="bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <img 
              src="/images/6.png" 
              alt="ALLTI" 
              className="h-10 mb-4"
            />
            <p className="text-gray-400">Soluciones de infraestructura TI especializadas en HPE</p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Enlaces</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Inicio</Link></li>
              <li><Link to="/nosotros" className="text-gray-400 hover:text-white transition-colors">Nosotros</Link></li>
              <li><Link to="/soluciones" className="text-gray-400 hover:text-white transition-colors">Soluciones</Link></li>
              <li><Link to="/casos-de-uso" className="text-gray-400 hover:text-white transition-colors">Casos de Uso</Link></li>
              <li><Link to="/contacto" className="text-gray-400 hover:text-white transition-colors">Contacto</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Soluciones</h3>
            <ul className="space-y-2">
              <li><Link to="/soluciones/nube-hibrida" className="text-gray-400 hover:text-white transition-colors">Nube Híbrida + IA</Link></li>
              <li><Link to="/soluciones/inteligencia-artificial" className="text-gray-400 hover:text-white transition-colors">Inteligencia Artificial</Link></li>
              <li><Link to="/soluciones/conectividad" className="text-gray-400 hover:text-white transition-colors">Conectividad</Link></li>
              <li><Link to="/soluciones/nube" className="text-gray-400 hover:text-white transition-colors">Nube y CloudOps</Link></li>
              <li><Link to="/soluciones/seguridad" className="text-gray-400 hover:text-white transition-colors">Seguridad</Link></li>
              <li><Link to="/soluciones/datos" className="text-gray-400 hover:text-white transition-colors">Gestión de Datos</Link></li>
              <li><Link to="/soluciones/servicios-gestionados" className="text-gray-400 hover:text-white transition-colors">Servicios Gestionados</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Contacto</h3>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-center space-x-2">
                <Mail size={18} />
                <span>lgarcia@allti.mx</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone size={18} />
                <span>+52 (55) 9000 5568</span>
              </li>
              <li className="flex items-center space-x-2">
                <Linkedin size={18} />
                <span>linkedin.com/company/allti</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2025 AG ALL TI S.R.L. de C.V. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
}

// Home Page
function Home() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1620121692029-d088224ddc74?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-blue-900/85 to-gray-900/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="max-w-5xl mx-auto">
            {/* Partner Badge */}
            <div className="inline-flex items-center gap-2 bg-hpe-green/20 backdrop-blur-sm px-4 md:px-5 py-2 md:py-2.5 rounded-full mb-6 md:mb-8 border border-hpe-green/40">
              <div className="w-2 h-2 bg-hpe-green rounded-full animate-pulse"></div>
              <span className="text-white font-semibold text-xs md:text-sm tracking-wide">Partner Certificado HPE desde 2010</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-6 md:mb-8 leading-tight text-white">
              Transformamos tu
              <br />
              <span className="bg-gradient-to-r from-allti-blue via-hpe-green to-allti-blue bg-clip-text text-transparent">
                Infraestructura TI
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-8 md:mb-12 text-gray-200 max-w-4xl mx-auto font-light leading-relaxed px-4">
              Soluciones empresariales de <span className="font-semibold text-white">clase mundial</span> con 
              tecnología <span className="font-semibold text-hpe-green">HPE</span>
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 md:gap-5 justify-center mb-12 md:mb-16 px-4">
              <Link 
                to="/soluciones" 
                className="group bg-allti-red text-white px-6 md:px-10 py-3 md:py-5 rounded-xl hover:bg-red-700 transition-all text-base md:text-lg font-semibold inline-flex items-center justify-center shadow-2xl shadow-allti-red/30 hover:shadow-allti-red/50 transform hover:scale-105"
              >
                Explorar Soluciones
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link 
                to="/contacto" 
                className="bg-white text-gray-900 px-6 md:px-10 py-3 md:py-5 rounded-xl hover:bg-gray-100 transition-all text-base md:text-lg font-semibold inline-flex items-center justify-center shadow-xl"
              >
                Hablar con un Experto
              </Link>
            </div>
            
            {/* Scroll indicator */}
            <div className="animate-bounce">
              <ChevronRight className="text-white/60 mx-auto rotate-90" size={32} />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nuestras Soluciones HPE</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ofrecemos soluciones completas de infraestructura TI con tecnología HPE de clase mundial
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow" data-testid="service-greenlake">
              <div className="bg-allti-blue/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Cloud className="text-allti-blue" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">HPE GreenLake</h3>
              <p className="text-gray-600 mb-4">
                Infraestructura como servicio que combina la agilidad de la nube con el control de on-premise.
              </p>
              <Link to="/soluciones" className="text-allti-blue font-semibold hover:underline inline-flex items-center">
                Conocer más <ChevronRight size={20} />
              </Link>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow" data-testid="service-servidores">
              <div className="bg-allti-red/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Server className="text-allti-red" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Servidores ProLiant</h3>
              <p className="text-gray-600 mb-4">
                Servidores de alto rendimiento diseñados para cargas de trabajo críticas y entornos empresariales.
              </p>
              <Link to="/soluciones" className="text-allti-blue font-semibold hover:underline inline-flex items-center">
                Conocer más <ChevronRight size={20} />
              </Link>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow" data-testid="service-almacenamiento">
              <div className="bg-hpe-green/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Database className="text-hpe-green" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Almacenamiento</h3>
              <p className="text-gray-600 mb-4">
                Soluciones de almacenamiento empresarial con tecnología flash y gestión inteligente de datos.
              </p>
              <Link to="/soluciones" className="text-allti-blue font-semibold hover:underline inline-flex items-center">
                Conocer más <ChevronRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">¿Por qué elegir ALLTI?</h2>
              <p className="text-lg text-gray-600 mb-8">
                Somos expertos certificados en soluciones HPE con años de experiencia en transformación digital empresarial.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-allti-blue/10 p-3 rounded-lg">
                    <Award className="text-allti-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Certificaciones HPE</h3>
                    <p className="text-gray-600">Equipo certificado en todas las soluciones HPE</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-allti-red/10 p-3 rounded-lg">
                    <Shield className="text-allti-red" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Soporte 24/7</h3>
                    <p className="text-gray-600">Asistencia técnica continua para tu tranquilidad</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-hpe-green/10 p-3 rounded-lg">
                    <Users className="text-hpe-green" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Consultoría Especializada</h3>
                    <p className="text-gray-600">Análisis y diseño de soluciones a medida</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg" 
                alt="Data Center" 
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-allti-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            ¿Listo para transformar tu infraestructura TI?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contáctanos hoy y descubre cómo podemos ayudarte a alcanzar tus objetivos empresariales
          </p>
          <Link to="/contacto" data-testid="footer-cta-btn" className="bg-white text-allti-blue px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-lg font-semibold inline-flex items-center">
            Solicitar Consultoría Gratuita <ChevronRight className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/nosotros" element={<Nosotros />} />
            <Route path="/soluciones" element={<Soluciones />} />
            <Route path="/soluciones/nube-hibrida" element={<NubeHibrida />} />
            <Route path="/soluciones/datos" element={<GestionDatos />} />
            <Route path="/soluciones/inteligencia-artificial" element={<InteligenciaArtificial />} />
            <Route path="/soluciones/conectividad" element={<Conectividad />} />
            <Route path="/soluciones/nube" element={<Nube />} />
            <Route path="/soluciones/seguridad" element={<Seguridad />} />
            <Route path="/soluciones/servicios-gestionados" element={<ServiciosGestionados />} />
            <Route path="/casos-de-uso" element={<CasosDeUso />} />
            <Route path="/contacto" element={<Contacto />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}
